import type { AdminChatReplyDto, AdminChatViewDto } from '@rimati/shared';

/**
 * O POST e o GET compartilham o mesmo formato visual. Esta borda também aceita
 * recibos idempotentes antigos, gravados antes de um campo aditivo existir, para
 * que o primeiro render nunca receba listas indefinidas nem uma jornada parcial.
 */
export function chatViewFromReply(reply: AdminChatReplyDto): AdminChatViewDto {
  const journey = reply.journey;
  return {
    session: reply.session,
    messages: Array.isArray(reply.messages) ? reply.messages : [],
    proposals: Array.isArray(reply.proposals) ? reply.proposals : [],
    tasks: Array.isArray(reply.tasks) ? reply.tasks : [],
    journey: journey && Array.isArray(journey.steps) ? journey : null,
  };
}

const CONFIRMATION_PREFIX = '[Confirmação na tela:';

type GuidanceMessage = { role: string; text: string; createdAt: string };

/**
 * Orientação anterior a uma alteração confirmada na tela fica recolhida, exceto
 * a última pergunta do Rimati que ainda espera resposta (variação, local, custo,
 * duração...): sem ela visível a pessoa não sabe o que responder.
 */
export function isStaleGuidance(
  messages: GuidanceMessage[],
  index: number,
  lastConfirmation = 0,
): boolean {
  const message = messages[index];
  const previous = messages[index - 1];
  if (message?.role !== 'assistant' || !previous?.text.startsWith(CONFIRMATION_PREFIX)) return false;
  const latest = Math.max(
    lastConfirmation,
    ...messages
      .filter((m) => m.role === 'user' && m.text.startsWith(CONFIRMATION_PREFIX))
      .map((m) => new Date(m.createdAt).getTime()),
  );
  if (new Date(previous.createdAt).getTime() >= latest) return false;
  /* A alteração confirmada foi a que o próprio Rimati propôs e uma resposta posterior já parte do
     estado novo: o alerta de "desatualizada" só confundiria. */
  const threadLatest = Math.max(
    ...messages
      .filter((m) => m.role === 'user' && m.text.startsWith(CONFIRMATION_PREFIX))
      .map((m) => new Date(m.createdAt).getTime()),
  );
  if (
    !endsWithRealQuestion(message.text) &&
    lastConfirmation <= threadLatest &&
    messages.some((m) => m.role === 'assistant' && new Date(m.createdAt).getTime() > threadLatest)
  )
    return false;
  return !isPendingQuestion(messages, index);
}

const COURTESY_CLOSING = /(?:algo mais|mais alguma(?: coisa)?|mais algum|posso ajudar|precisa de mais|em que mais|quer ajuda com mais)/i;

/** A última frase termina em "?" de verdade: sem contar "?" de URL e sem fecho de cortesia. */
export function endsWithRealQuestion(text: string): boolean {
  const clean = text.replace(/https?:\/\/\S+/gi, ' ').trim().replace(/["')\]*_\s]+$/, '');
  if (!clean.endsWith('?')) return false;
  const sentence = clean.slice(0, -1).split(/[.!?\n]/).pop() ?? '';
  return !COURTESY_CLOSING.test(sentence);
}

/** Última fala do Rimati, com pergunta, sem resposta real da pessoa depois dela. */
export function isPendingQuestion(messages: GuidanceMessage[], index: number): boolean {
  const message = messages[index];
  if (message?.role !== 'assistant' || !endsWithRealQuestion(message.text)) return false;
  for (let i = index + 1; i < messages.length; i += 1) {
    const next = messages[i]!;
    if (next.role === 'assistant') return false;
    if (next.role === 'user' && !next.text.startsWith(CONFIRMATION_PREFIX)) return false;
  }
  return true;
}
